import { useQueryClient } from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import { IconEllipsisVertical, IconPlus } from "~/components/icons";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import VendorTable from "~/components/vendor/table/vendor-table";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import { VendorsResource } from "~/features/vendor/vendor.resource";
import { getVendorsPageData } from "~/features/vendor/vendors-page";
import { withBusinessGuard } from "~/lib/route-guards";

export const Route = createFileRoute("/_dashboard/dashboard/vendors/")({
  ...withBusinessGuard(),
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;
    const data = await getVendorsPageData({ data: { businessId } });
    // seedIfEmpty (not seed) so a fresh client-side mutation (e.g. a rename
    // that optimistically updated the list cache) isn't clobbered by stale
    // loader data when navigating back to the table.
    VendorsResource.seedIfEmpty(
      context.queryClient,
      { businessId, limit: 20 },
      data.vendors,
    );
    businessDetailResource.seed(
      context.queryClient,
      businessId,
      data.businessDetail,
    );
    return {
      businessId,
      vendors: data.vendors,
      activeBusiness: data.businessDetail,
    };
  },
  component: VendorsPage,
});

function VendorsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const data = Route.useLoaderData();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().activeBusiness,
  );
  VendorsResource.seedIfEmpty(
    qc,
    { businessId: data().businessId, limit: 20 },
    data().vendors,
  );

  const businessId = useActiveBusinessId();

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold">Vendors</h1>
          <p class="text-muted-foreground">
            Manage your vendors and suppliers.
          </p>
        </div>
        <DropdownMenu placement="bottom-end">
          <DropdownMenuTrigger
            as={Button<"button">}
            variant="outline"
            size="icon"
          >
            <IconEllipsisVertical class="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-44">
            <DropdownMenuItem
              onClick={() =>
                navigate({
                  to: "/dashboard/vendors/$vendorId",
                  params: { vendorId: "new" },
                })
              }
            >
              <IconPlus class="size-4" />
              Add Vendor
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <VendorTable businessId={businessId() ?? ""} />
    </div>
  );
}
